// Shared tooltip/grid/axis styling for all chart wrappers so the 5 chart
// types read as one consistent system, themed via mo3tiThemes conventions.
export const getChartTheme = (theme) => {
  const isDark = theme === "dark";
  return {
    isDark,
    grid: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
    axis: isDark ? "rgba(255,255,255,0.5)" : "rgba(30,41,59,0.55)",
    tooltipStyle: {
      background: isDark ? "rgba(15,23,42,0.95)" : "rgba(255,255,255,0.98)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 12,
      boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.5)" : "0 12px 32px rgba(0,0,0,0.12)",
      backdropFilter: "blur(12px)",
      color: isDark ? "#f1f5f9" : "#1e293b",
      fontSize: 12,
      padding: "8px 12px",
    },
    labelStyle: { color: isDark ? "#cbd5e1" : "#64748b", fontWeight: 600, marginBottom: 4 },
  };
};

export const CHART_PALETTE = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

/**
 * Computes a Set of indices corresponding to the most significant peak data
 * points in a chart dataset, so the x-axis only labels the handful of
 * points that actually matter instead of every single name.
 *
 * A point only qualifies as a candidate peak if it is a STRICT local maximum
 * (bigger than both neighbors -- plateaus/ties no longer count, since those
 * were flagging almost every point on noisy real-world data). The global
 * max is always guaranteed to be included. If more candidates remain than
 * `maxPeaks`, only the highest-value ones are kept, so the label count stays
 * small and predictable regardless of how noisy the dataset is.
 */
export const getPeakIndices = (data = [], maxPeaks = 5) => {
  if (!Array.isArray(data) || data.length === 0) return new Set();

  const values = data.map((d) => {
    const val = Number(d?.value);
    return isNaN(val) ? 0 : val;
  });
  const n = values.length;
  if (n === 1) return new Set([0]);

  const candidates = [];

  for (let i = 0; i < n; i++) {
    const val = values[i];
    const prev = i > 0 ? values[i - 1] : null;
    const next = i < n - 1 ? values[i + 1] : null;

    let isPeak = false;

    if (prev === null && next !== null) {
      if (val > next) isPeak = true;
    } else if (next === null && prev !== null) {
      if (val > prev) isPeak = true;
    } else if (prev !== null && next !== null) {
      // Strict local max only -- plateaus/ties are intentionally excluded
      // so flat/noisy stretches of data don't get labeled as "peaks".
      if (val > prev && val > next) isPeak = true;
    }

    if (isPeak) candidates.push(i);
  }

  const maxVal = Math.max(...values);
  const maxIdx = values.indexOf(maxVal);

  // The single highest point is always the most important one to label.
  if (maxVal > 0 && !candidates.includes(maxIdx)) {
    candidates.push(maxIdx);
  }

  if (candidates.length === 0) {
    return new Set([maxIdx >= 0 ? maxIdx : 0]);
  }

  // Two "real" peaks sitting right next to each other on the axis still
  // render as overlapping/touching text, even though each one individually
  // passed the peak check. Enforce a minimum index gap between chosen
  // labels -- picking highest-value peaks first -- so selected labels are
  // always spread out enough to stay readable.
  const minGap = Math.max(1, Math.ceil(n / (maxPeaks * 2)));
  const byValueDesc = [...candidates].sort((a, b) => values[b] - values[a]);
  const selected = [];
  for (const idx of byValueDesc) {
    if (selected.length >= maxPeaks) break;
    if (selected.every((s) => Math.abs(s - idx) >= minGap)) {
      selected.push(idx);
    }
  }
  if (selected.length === 0) selected.push(byValueDesc[0]);

  return new Set(selected);
};

